import { useMemo } from 'react'
import { useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { TreatmentInfo } from '@customTypes/TreatmentInfo'
import useTreatment from '@hooks/useTreatment'
import { usePractitionersContext } from '@context/PractitionersContext'
import { useRecommendedPractitioner } from './useRecommendedPractitioner'

export const useGetTreatmentInfo = (): TreatmentInfo => {
  const { clinicId } = useConfig()
  const {
    treatmentBundleTypeReferenceId,
    selectedBookableDateAndTime,
    preferredPractitionerReferenceIds,
    appointmentDuration,
  } = useChoiceState()
  const { data: treatment } = useTreatment(clinicId, treatmentBundleTypeReferenceId)
  const { allPractitioners, isLoading: practitionersLoading } = usePractitionersContext()
  const { data: recommendedPractitioner, isLoading: recommendedPractitionerLoading } = useRecommendedPractitioner()
  const isLoading = practitionersLoading || recommendedPractitionerLoading

  const practitioner = useMemo(() => {
    if (recommendedPractitioner) {
      return recommendedPractitioner
    } else if (preferredPractitionerReferenceIds.length === 1) {
      return allPractitioners?.find((practitioner) => practitioner.referenceId === preferredPractitionerReferenceIds[0])
    } else {
      return undefined
    }
  }, [allPractitioners, preferredPractitionerReferenceIds, recommendedPractitioner])

  const treatmentInfo: TreatmentInfo = useMemo(
    () => ({
      treatment,
      practitioner,
      selectedAppointment: selectedBookableDateAndTime,
      allPractitioners,
      selectedPractitionerIds: preferredPractitionerReferenceIds,
      appointmentDuration: appointmentDuration,
      isLoading,
    }),
    [
      treatment,
      practitioner,
      selectedBookableDateAndTime,
      allPractitioners,
      preferredPractitionerReferenceIds,
      appointmentDuration,
      isLoading,
    ]
  )

  return treatmentInfo
}
